import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
  type CSSProperties,
  type PointerEvent,
  type ReactNode,
} from "react";
import * as RadixDialog from "@radix-ui/react-dialog";
import s from "./Sheet.module.css";

interface SheetState {
  /** Closes the sheet through the caller's own `onOpenChange`, or null for an
      uncontrolled sheet, which has none to call. */
  requestClose: (() => void) | null;
  /** The caller's `open`, or undefined for an uncontrolled sheet. */
  open: boolean | undefined;
}

const SheetContext = createContext<SheetState>({ requestClose: null, open: undefined });

/** Wraps `Dialog.Root` only to also hand its `open`/`onOpenChange` to
    `SheetContent` via context — that's what lets a drag-to-dismiss gesture on
    the handle close the sheet by calling the same function the app already
    passed in, and lets the sheet tell a dismissal it started from one it
    didn't, rather than needing Radix's own (unexported) internal state. Only
    works for a controlled sheet (`open`/`onOpenChange` both passed, as every
    current caller does); an uncontrolled sheet still opens/closes normally,
    it just can't be dismissed by dragging the handle down. */
export function Sheet({ onOpenChange, ...props }: ComponentPropsWithoutRef<typeof RadixDialog.Root>) {
  const requestClose = onOpenChange ? () => onOpenChange(false) : null;
  return (
    <SheetContext.Provider value={{ requestClose, open: props.open }}>
      <RadixDialog.Root onOpenChange={onOpenChange} {...props} />
    </SheetContext.Provider>
  );
}

export const SheetTrigger = RadixDialog.Trigger;
export const SheetClose = RadixDialog.Close;
export const SheetTitle = RadixDialog.Title;
export const SheetDescription = RadixDialog.Description;

export interface SheetContentProps extends ComponentPropsWithoutRef<typeof RadixDialog.Content> {
  children: ReactNode;
  /** Where the sheet portals to. Pass the element `#root` renders into, not
      `document.body`: `#root` is sized to the visible viewport (not the
      window) so a client app with a software keyboard stays correct. See
      `platform/docs/symptom-tracker.md` Phase 3 for the trace that found
      this.

      Must have a definite height: the sheet's own cap and its full-screen
      mode are percentages of this element, deliberately rather than viewport
      units. A container with an auto height leaves the sheet uncapped. */
  container: HTMLElement | null;
  /** Full-screen state. Omit both to let the handle drag manage it
      internally; pass both when a caller needs to force full screen (e.g.
      the full symptom checklist opens already expanded) while the handle
      drag still collapses/expands it from there. */
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  /** How much of the sheet a software keyboard is covering, in px, or 0 when
      none is. The caller measures it (the client app's
      useSheetKeyboardOffset); the sheet decides what to do about it, which is
      two things at once:

      it adds that much scrollable space below its content (a spacer element —
      see `.keyboardSpacer`), so the fields the keyboard covers can still be
      scrolled up to, and it freezes its own height for as long as the
      keyboard is there. The freeze is the point.
      Sizing to content means that extra space would otherwise grow the sheet
      into its `max-height` cap, and a sheet whose top edge moves when a field
      is tapped is the bug this whole prop exists to avoid — the keyboard is
      an overlay, and a sheet should sit still underneath it.

      Kept here rather than done by the caller because size is size: two
      owners writing heights and paddings onto this element from opposite
      sides is what made this hard to get right. */
  keyboardHeight?: number;
}

// Distance-based snap thresholds for the handle drag, each a fraction of the
// gesture's own reference height rather than a fixed pixel count, so they
// scale with whatever the sheet's natural (content-driven) height happens to
// be for that view.
const EXPAND_THRESHOLD = 0.8; // dragged past 80% of the viewport height -> full screen
const COLLAPSE_BACK_THRESHOLD = 0.75; // dragging down from full screen below 75% of viewport -> back to natural height
const CLOSE_THRESHOLD = 0.6; // dragging down below 60% of where the drag started -> dismiss, from full screen as much as from the natural height

/** Matches the height transition in Sheet.module.css. Used only as a fallback
    for releasing the pinned height if `transitionend` never arrives (the
    animation is interrupted, the tab is hidden mid-way). */
const RESIZE_MS = 200;

/** Matches `--sheet-exit-duration` in Sheet.module.css, plus a frame of slack
    so the height a dismissing drag was pinned at is let go once Radix has
    unmounted the sheet, rather than while it is still sliding out. */
const EXIT_MS = 350;

export function SheetContent({
  className,
  children,
  container,
  style,
  expanded: expandedProp,
  onExpandedChange,
  keyboardHeight = 0,
  ...rest
}: SheetContentProps) {
  const { requestClose, open } = useContext(SheetContext);
  const [internalExpanded, setInternalExpanded] = useState(false);
  const expanded = expandedProp ?? internalExpanded;
  const [dragHeight, setDragHeight] = useState<number | null>(null);
  /** The height a dismissing drag ended at, held for the duration of the
      slide-out — see the close branch in `onUp`. */
  const [exitHeight, setExitHeight] = useState<number | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const dragHeightRef = useRef<number | null>(null);
  /** The height the sheet was at before the render in progress, which is what
      a resize has to animate *from*: by the time a layout effect can see a
      size change, the DOM already reports the new size. */
  const previousHeight = useRef<number | null>(null);
  const resizeCleanup = useRef<(() => void) | null>(null);
  const resizeTarget = useRef<number | null>(null);
  /** The height to hold while a keyboard is up — see `keyboardHeight`. */
  const frozenHeight = useRef<number | null>(null);

  /** The height the sheet is being held at by the gesture — the live drag, or
      the point a dismissing drag released at, which the slide-out then carries
      on from. Either way it overrides the sheet's own sizing.

      The released height counts only while the sheet is actually on its way
      out. Gating it on `open` here rather than clearing it in an effect is
      what keeps a pin that has outlived its dismissal — the caller declined
      it, or reopened the sheet before the slide-out finished — from ever
      reaching the DOM. */
  const heldHeight = dragHeight ?? (open === false ? exitHeight : null);

  /* Read during render, on purpose: this runs before React commits this
     render's DOM changes, so it is the last moment the old size is still
     readable. */
  if (contentRef.current && heldHeight === null && resizeCleanup.current === null) {
    previousHeight.current = contentRef.current.getBoundingClientRect().height;
  }

  /* Taken in the same breath, and deliberately before the padding this render
     is about to add: the height to hold is the one from *before* the keyboard,
     and by the next commit it would already include the extra space. */
  if (keyboardHeight <= 0) {
    frozenHeight.current = null;
  } else if (frozenHeight.current === null) {
    frozenHeight.current = previousHeight.current;
  }
  const frozen = keyboardHeight > 0 ? frozenHeight.current : null;

  function setExpanded(next: boolean) {
    if (expandedProp === undefined) setInternalExpanded(next);
    onExpandedChange?.(next);
  }

  /** Reads the height `.content` would settle at right now with none of our
      inline overrides applied — mutating the live node's style and reverting
      it before anything else can read it, so nothing ever paints the
      intermediate state. Class-level sizing (`max-height`, and `.expanded`
      when it applies) is deliberately left in place: it is part of what the
      sheet is settling to. */
  function measureRestingHeight(): number {
    const el = contentRef.current;
    if (!el) return 0;
    const previousStyle = el.getAttribute("style");
    el.style.height = "";
    el.style.minHeight = "";
    el.style.maxHeight = "";
    el.style.transition = "none";
    const height = el.getBoundingClientRect().height;
    if (previousStyle === null) el.removeAttribute("style");
    else el.setAttribute("style", previousStyle);
    return height;
  }

  /** Animates the sheet from the height it was at to the height it now wants,
      by pinning the old height inline for one frame and then transitioning to
      the new one.

      Every resize goes through here rather than through CSS alone, because
      `.content`'s resting height is intrinsic — `max-height` capping an auto
      height — and a transition can neither start from nor end at `auto`.
      Without the pin, going full screen, collapsing back out of it and
      swapping to taller content all snapped, which does not read as a native
      sheet. */
  function animateResize(from: number, to: number) {
    const el = contentRef.current;
    if (!el || Math.abs(to - from) < 1) return;

    resizeCleanup.current?.();
    resizeTarget.current = to;

    el.style.transition = "none";
    el.style.height = `${from}px`;
    el.style.minHeight = "0px";
    el.style.maxHeight = "none";

    const frame = requestAnimationFrame(() => {
      el.style.transition = "";
      el.style.height = `${to}px`;
    });

    const release = () => {
      cancelAnimationFrame(frame);
      clearTimeout(timer);
      el.removeEventListener("transitionend", onEnd);
      resizeCleanup.current = null;
      resizeTarget.current = null;
      // Back to intrinsic sizing, so later content changes size the sheet
      // again — the pixel height it lands on is the one the classes produce
      // anyway, so dropping it moves nothing.
      el.style.transition = "";
      el.style.height = "";
      el.style.minHeight = "";
      el.style.maxHeight = "";
    };

    const onEnd = (event: TransitionEvent) => {
      if (event.target !== el || event.propertyName !== "height") return;
      release();
    };

    const timer = setTimeout(release, RESIZE_MS * 2);
    el.addEventListener("transitionend", onEnd);
    resizeCleanup.current = release;
  }

  /* Catches every resize that isn't the drag's own — a mode toggle, a content
     swap, the height a drag left behind settling back — and runs before
     paint, so the pin lands without the new size ever being shown. */
  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) {
      // Closed, so Radix has unmounted the content. Forget the height it had:
      // the next open is a slide-in from nothing, not a resize from wherever
      // the previous one happened to end.
      previousHeight.current = null;
      resizeCleanup.current?.();
      return;
    }
    if (dragHeight !== null) return;
    // Closing: the slide-out owns the motion — starting, when a drag put the
    // sheet here, from the height it released at — and resizing underneath it
    // would fight that.
    if (el.dataset.state === "closed") return;
    // Frozen under a keyboard: holding the height is the whole point, so
    // there is nothing to animate to and nothing to remember.
    if (frozen !== null) return;

    const to = measureRestingHeight();

    if (resizeCleanup.current !== null) {
      // Already animating. Same destination: let it finish. A new one:
      // retarget from wherever this frame has it.
      if (resizeTarget.current !== null && Math.abs(resizeTarget.current - to) < 1) return;
      animateResize(el.getBoundingClientRect().height, to);
      return;
    }

    const from = previousHeight.current;
    previousHeight.current = to;
    if (from === null) return; // first paint: the slide-in covers it
    animateResize(from, to);
  });

  useLayoutEffect(() => () => resizeCleanup.current?.(), []);

  /* The slide-out has no end this component can observe — Radix unmounts the
     node when it finishes, without re-rendering us — so the released height is
     let go on a timer, and at once if the sheet is open again before that.
     Forgetting it matters: a later close the drag had nothing to do with
     (Escape, the overlay, a save) has to slide out from full size. */
  useEffect(() => {
    if (exitHeight === null) return;
    if (open !== false) {
      setExitHeight(null);
      return;
    }
    const timer = setTimeout(() => setExitHeight(null), EXIT_MS);
    return () => clearTimeout(timer);
  }, [exitHeight, open]);

  function handleGrab(event: PointerEvent<HTMLDivElement>) {
    if (!contentRef.current) return;
    resizeCleanup.current?.();
    const start = { clientY: event.clientY, height: contentRef.current.getBoundingClientRect().height };

    function onMove(moveEvent: globalThis.PointerEvent) {
      const delta = start.clientY - moveEvent.clientY;
      const next = Math.max(0, start.height + delta);
      dragHeightRef.current = next;
      setDragHeight(next);
    }

    function cleanup() {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
    }

    function onUp() {
      cleanup();
      const height = dragHeightRef.current;
      dragHeightRef.current = null;
      setDragHeight(null);
      if (height === null) return;
      const viewportHeight = window.innerHeight;
      if (requestClose && height < start.height * CLOSE_THRESHOLD) {
        // Checked before the snap branches, and from full screen too: a drag
        // that carries the sheet most of the way off the screen is a dismissal
        // wherever it started. Collapsing back to the natural height instead,
        // and only closing on a second drag, is the bug this ordering fixes.
        //
        // Straight to closed: the slide-out in Sheet.module.css is what
        // animates a dismissal, and Radix holds the node mounted until it has
        // finished. Nothing to settle back to — but the sheet does have to
        // leave from where the finger let go, so the released height is pinned
        // for the slide-out rather than dropped here. Dropping it let the
        // sheet spring back to full size for the frames before the slide-out
        // took over, which read as a jump back open and then a close.
        // slideDown translates by 100% of the element, so the shorter sheet
        // also has proportionally less distance to travel.
        setExitHeight(height);
        // Or the next open comes back full screen, having been dismissed from
        // there rather than collapsed first.
        setExpanded(false);
        requestClose();
      } else if (expanded) {
        if (height < viewportHeight * COLLAPSE_BACK_THRESHOLD) setExpanded(false);
      } else if (height > viewportHeight * EXPAND_THRESHOLD) {
        setExpanded(true);
      }
      // Anything else falls through to the resize effect, which animates the
      // sheet back from where the gesture left it to its resting height.
    }

    function onCancel() {
      cleanup();
      dragHeightRef.current = null;
      setDragHeight(null);
    }

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp, { once: true });
    window.addEventListener("pointercancel", onCancel, { once: true });
  }

  const classes = [s.content, expanded && s.expanded, className].filter(Boolean).join(" ");
  // The keyboard's own space always applies; `.content` does the arithmetic
  // with it, so its own padding and safe-area inset stay in one place.
  const base = { ...style, "--sheet-keyboard-height": `${Math.max(0, keyboardHeight)}px` };
  let mergedStyle: CSSProperties = base;
  if (heldHeight !== null) {
    mergedStyle = {
      ...base,
      height: `${heldHeight}px`,
      // The floor animateResize may have left behind has to come off, or a
      // drag can't take the sheet under it to dismiss.
      minHeight: 0,
      maxHeight: "none",
      // Turns off the height transition only. The slide-out is a keyframe
      // animation, so it still plays over a sheet held at the height a
      // dismissing drag released it at.
      transition: "none",
    };
  } else if (frozen !== null) {
    // Held at the height it had before the keyboard: the cap is lifted so the
    // added space cannot push the sheet into it, and the transition is off so
    // the hold is exact rather than something to animate.
    mergedStyle = {
      ...base,
      height: `${frozen}px`,
      minHeight: 0,
      maxHeight: "none",
      transition: "none",
    };
  }

  return (
    <RadixDialog.Portal container={container}>
      <RadixDialog.Overlay className={s.overlay} />
      <RadixDialog.Content
        ref={contentRef}
        className={classes}
        style={mergedStyle}
        {...rest}
      >
        {/* Pull up to fill the screen, pull down to dismiss — like a native
            bottom sheet's grabber. Drag-only: it stays out of the tab order,
            since Escape and the overlay already cover keyboard/AT dismissal. */}
        <div className={s.handleHit} aria-hidden="true" onPointerDown={handleGrab}>
          <div className={s.handle} />
        </div>
        {children}
        {/* Only while a keyboard is up, so nothing is scrollable that
            shouldn't be — see `.keyboardSpacer` for why this is an element and
            not padding. */}
        {keyboardHeight > 0 && <div className={s.keyboardSpacer} aria-hidden="true" />}
      </RadixDialog.Content>
    </RadixDialog.Portal>
  );
}
